import { cva } from "class-variance-authority"
import Button from "./Button.svelte"

export type { ButtonProps } from "./Button.svelte"
export { Button }

const variant = {
  primary:
    "bg-button-blue text-icon-general-inverted hover:bg-button-blue-hovered active:bg-button-blue-hovered",
  secondary:
    "bg-button-primary text-button-primary hover:bg-button-primary-hovered active:bg-button-primary-hovered",
  success:
    "bg-button-green text-icon-general-inverted hover:bg-button-green-hovered active:bg-button-green-hovered",
  error:
    "bg-button-red text-icon-general-inverted hover:bg-button-red-hovered active:bg-button-red-hovered",
  notice:
    "bg-button-orange text-icon-general-inverted hover:bg-button-orange-hovered active:bg-button-orange-hovered",
  pending:
    "bg-button-gold text-icon-general-inverted hover:bg-button-gold-hovered active:bg-button-gold-hovered",
  ghostPrimary:
    "hover:bg-button-primary-hovered active:bg-button-primary-hovered hover:text-accent-foreground active:text-accent-foreground disabled:bg-transparent text-icon-general-primary",
  ghostSecondary:
    "hover:bg-button-primary-hovered active:bg-button-primary-hovered hover:text-accent-foreground active:text-accent-foreground disabled:bg-transparent text-icon-general-secondary",
  ghostAccent:
    "text-icon-semantic-link hover:text-icon-semantic-link-hovered hover:bg-button-primary-hovered active:text-icon-semantic-link-hovered active:bg-button-primary-hovered disabled:bg-transparent",
  link: "[disabled]:text-button-inactive text-icon-general-blue focus-visible:underline hover:underline underline-offset-[3px] active:underline active:scale-100 focus-visible:active:underline disabled:bg-transparent",
}

const PSEUDO_BUTTON_VARIANTS = [
  "ghostPrimary",
  "ghostSecondary",
  "ghostAccent",
  "link",
] satisfies (keyof typeof variant)[]

const size = {
  small: "font-small-400 px-2.5 py-1.5",
  medium: "font-medium-400 px-3 py-1.5",
  large: "font-large-400 px-4 py-2",
}

export const buttonIconVariants = cva(
  "inline-flex shrink-0 items-center justify-center [&_svg]:size-full",
  {
    variants: {
      size: {
        small: "size-4",
        medium: "size-5",
        large: "size-6",
      },
    },
    defaultVariants: {
      size: "large",
    },
  }
)

export const buttonIconGapVariants = cva("", {
  variants: {
    size: {
      small: "gap-x-1",
      medium: "gap-x-1.5",
      large: "gap-x-1.5",
    },
  },
  defaultVariants: {
    size: "large",
  },
})

export const buttonVariantKeys = Object.keys(
  variant
) as (keyof typeof variant)[]
export const buttonSizeKeys = Object.keys(size) as (keyof typeof size)[]

export const buttonVariants = cva(
  `border-semantic-transparent focus-visible:outline-semantic-interaction-selected disabled:bg-button-disabled disabled:text-icon-general-disabled inline-flex items-center justify-center rounded border align-middle transition-[background-color,transform,translate,scale,rotate] duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] focus-visible:outline-1 focus-visible:outline-offset-0 active:scale-95 disabled:pointer-events-none`,
  {
    variants: {
      variant,
      size,
      iconOnly: { true: "", false: "" },
    },
    compoundVariants: [
      { size: "small", iconOnly: true, className: "p-1.5" },
      { size: "medium", iconOnly: true, className: "p-1.5" },
      { size: "large", iconOnly: true, className: "p-2" },
      {
        variant: PSEUDO_BUTTON_VARIANTS,
        className: "h-fit w-fit min-w-fit border-0 p-0",
      },
      {
        variant: ["link"],
        className: "align-baseline",
      },
    ],
    defaultVariants: { iconOnly: false },
  }
)
